import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from 'components/icons';
import { format, formatDistanceToNow, isValid, max, parseISO } from 'date-fns';
import { compact } from 'lodash';
import querystring from 'querystring';
import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from 'store';
import {
  useGetCustomerSegmentsQuery,
  useGetStoreInfoQuery,
  useRemoveIntegrationMutation,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';
import { useProviderIcon } from './useProviderIcon';

interface ProviderDetailsProps {
  provider: string;
}

// * whole component needs revision for a more scalable solution, pending on Backend DTO update from individual props to array of objects
export const ProviderDetails = ({ provider }: ProviderDetailsProps) => {
  const [howToExpanded, setHowToExpanded] = useState(false);

  const location = useLocation();

  const { ProviderIcon } = useProviderIcon({ provider });

  const { currentStore } = useAppSelector(userDetailsState);

  const { data: segments, isFetching } = useGetCustomerSegmentsQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const { data: storeInfo } = useGetStoreInfoQuery(
    currentStore.serializedStoreName,
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const providerAllSegmentsLastSynced = compact(
    segments
      ?.filter((segment) =>
        segment.integrations.find(
          (item) =>
            item.provider.toLowerCase() === provider.toLowerCase() &&
            item.last_synced
        )
      )
      .flatMap((segment) => segment.integrations.map((i) => i.last_synced))
  );

  const providerLastSync = max(
    providerAllSegmentsLastSynced.map((item) => parseISO(item))
  );

  const providerSegmentsAutoSyncCount = segments?.filter((segment) =>
    segment.integrations.find(
      (item) =>
        item.provider.toLowerCase() === provider.toLowerCase() &&
        item.auto_synced
    )
  ).length;

  const lastSync =
    segments?.length && isValid(providerLastSync) ? (
      formatDistanceToNow(providerLastSync, {
        addSuffix: true,
      })
    ) : (
      <Link
        to={{ pathname: '/customers/segments', search: location.search }}
        className="font-bold text-segments-link"
      >
        Go to My Segments to sync
      </Link>
    );

  const currentProviderInfo = storeInfo?.integrations?.find(
    (item) => item.provider.toLowerCase() === provider.toLowerCase()
  );

  const connectionStatus = currentProviderInfo?.connection_status;

  return (
    <div className="w-full pt-10 pl-10 pr-10 bg-white border shadow-sm lg:w-segments-500px rounded-2xl">
      {/* Card Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {ProviderIcon}
          <p className="text-2xl capitalize">{provider}</p>
        </div>
        {!!!connectionStatus && (
          <Link
            to={{
              pathname: `/integrations/connect/${provider.toLowerCase()}`,
              state: { background: location },
              search: location.search,
            }}
            className="bg-segments-accent-color-1 text-white py-1.5 px-4 rounded-lg font-bold"
          >
            Reconnect
          </Link>
        )}
        <SettingsMenu provider={provider}>
          <MenuIcon />
        </SettingsMenu>
      </div>

      {/* Card Body */}
      <div className="mt-4">
        <div className="grid grid-cols-2 py-4">
          <p className="text-black text-opacity-50">date added</p>
          <p>
            {currentProviderInfo?.created_at &&
              format(parseISO(currentProviderInfo.created_at), 'yyyy-MM-dd')}
          </p>
        </div>

        <hr />

        <div className="grid grid-cols-2 py-4">
          <p className="text-black text-opacity-50">auto-synced segments</p>
          <p>{providerSegmentsAutoSyncCount}</p>
        </div>

        <hr />

        <div className="grid grid-cols-2 py-4">
          <p className="text-black text-opacity-50">last synced</p>
          <p>{isFetching ? 'Loading...' : lastSync}</p>
        </div>

        <hr />

        <div className="grid grid-cols-2 py-4">
          <p className="text-black text-opacity-50">connection status</p>
          <p>
            {connectionStatus ? (
              'Connected'
            ) : (
              <span className="font-bold text-segments-danger">
                Connection error
              </span>
            )}
          </p>
        </div>

        {/* Card Footer */}
        <div className="py-4">
          <div
            className="flex items-center space-x-2"
            onClick={(e) => setHowToExpanded(!howToExpanded)}
          >
            <p className="text-xl">How do I use this?</p>
            <span className="w-4 h-4 stroke-2">
              {howToExpanded ? (
                <ChevronUpIcon className="" />
              ) : (
                <ChevronDownIcon className="" />
              )}
            </span>
          </div>

          {howToExpanded && (
            <p className="pt-2 text-black text-opacity-60">
              Go to{' '}
              <Link
                to={{
                  pathname: '/customers/segments',
                  search: location.search,
                }}
                className="font-bold text-segments-link"
              >
                My Segments
              </Link>{' '}
              to turn on {provider} syncing for individual segments. Once turned
              on, a segment will automatically sync new data with {provider} on
              a regular basis, but you can also manually trigger a sync at any
              time from My Segments.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

function MenuIcon() {
  return (
    <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 11.016a1.016 1.016 0 100-2.032 1.016 1.016 0 000 2.032zM13.516 11.016a1.016 1.016 0 100-2.032 1.016 1.016 0 000 2.032zM6.484 11.016a1.016 1.016 0 100-2.032 1.016 1.016 0 000 2.032z"
        fill="#000"
      />
      <path
        d="M17.5 10c0-4.14-3.36-7.5-7.5-7.5-4.14 0-7.5 3.36-7.5 7.5 0 4.14 3.36 7.5 7.5 7.5 4.14 0 7.5-3.36 7.5-7.5z"
        stroke="#000"
        strokeMiterlimit="10"
      />
    </svg>
  );
}

function SettingsMenu({
  children,
  provider,
}: {
  children: ReactNode;
  provider: string;
}) {
  const { query } = useParseURLQueryParams();
  const location = useLocation();

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const [removeIntegration] = useRemoveIntegrationMutation();

  const handleDelete = () => {
    removeIntegration({
      storeId: Number(storeInfo?.id),
      provider: provider.toLowerCase(),
    });
  };
  return (
    <div className="relative">
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button className="flex items-center justify-center w-8 h-8 rounded-full focus:outline-none hover:bg-segments-gray-F1">
              {children}
            </Menu.Button>

            <Transition
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                static
                className="absolute right-0 w-56 origin-bottom-right bg-white border border-gray-200 rounded-md shadow-lg outline-none "
              >
                <Menu.Item
                  as={Link}
                  to={{
                    pathname: `/integrations/connect/${provider.toLowerCase()}`,
                    state: { background: location },
                    search: location.search,
                  }}
                  className="flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1"
                >
                  Reconnect
                </Menu.Item>
                <Menu.Item
                  as="button"
                  className="flex justify-between w-full px-4 py-2 text-sm leading-5 text-left text-gray-800 focus:outline-none hover:bg-segments-gray-F1"
                  onClick={handleDelete}
                >
                  Delete
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}
